import type * as d3 from 'd3';
import { create } from 'zustand';

import type { NodeEntity } from '@/components/CourseMap/coursemap.types';

export interface HistoryEntry {
	snapshot: string;
	label: string;
	timestamp: Date;
}

interface ICourseMapStore {
	// ── Layout / D3 ─────────────────────────────────────────────────────────
	courseCoordsArray: NodeEntity[];
	/** Full tree for navbar search (includes `_children` / collapsed branches). */
	courseSearchNodes: NodeEntity[];
	d3InstanceRef: React.MutableRefObject<d3.Selection<
		SVGSVGElement | null,
		unknown,
		null,
		undefined
	> | null>;
	zoom: React.MutableRefObject<d3.ZoomBehavior<SVGSVGElement, unknown> | null>;
	duration: number;
	setCourseCoordsArray: (coordsArray: NodeEntity[]) => void;
	setCourseSearchNodes: (nodes: NodeEntity[]) => void;
	/** Clears coords/search arrays and nulls zoom + SVG refs (call when CourseMap unmounts). */
	clearCourseMapTransientState: () => void;
	/**
	 * When true, the next discipline D3 click skips `shouldFitViewRef` (fit-all zoom).
	 * Map search sets this before programmatic expand so only the search pan runs.
	 */
	suppressNextDisciplineFitView: boolean;
	setSuppressNextDisciplineFitView: (value: boolean) => void;

	// ── Edit history ─────────────────────────────────────────────────────────
	// `past`   — states before the current one (oldest first)
	// `future` — states that were undone (most-recently-undone first)
	// The component owns `treeData` (useState) and calls pushHistory / undo /
	// redo; the store only manages the stacks and returns snapshots to restore.
	past: HistoryEntry[];
	future: HistoryEntry[];

	/**
	 * Call before every `setTreeData`. Pushes the *current* snapshot onto the
	 * undo stack and clears the redo stack (a new action invalidates any
	 * previously undone states).
	 */
	pushHistory: (currentSnapshot: string, label: string) => void;

	/**
	 * Moves the most-recent past entry to the front of future and returns its
	 * snapshot so the component can call `setTreeData` with it.
	 * Returns `null` if there is nothing to undo.
	 */
	undo: (presentSnapshot: string) => string | null;

	/**
	 * Moves the front of future back onto past and returns its snapshot.
	 * Returns `null` if there is nothing to redo.
	 */
	redo: (presentSnapshot: string) => string | null;

	/** Wipes both stacks — call after a successful save. */
	clearHistory: () => void;
}

const useCourseMapStore = create<ICourseMapStore>((set, get) => ({
	// ── Layout / D3 ─────────────────────────────────────────────────────────
	courseCoordsArray: [],
	courseSearchNodes: [],
	d3InstanceRef: { current: null },
	zoom: { current: null },
	duration: 750,
	setCourseCoordsArray: (coordsArray) => {
		set({ courseCoordsArray: [...coordsArray] });
	},
	setCourseSearchNodes: (nodes) => {
		set({ courseSearchNodes: [...nodes] });
	},

	clearCourseMapTransientState: () => {
		set({ courseCoordsArray: [], courseSearchNodes: [], suppressNextDisciplineFitView: false });
		const { zoom, d3InstanceRef } = get();
		zoom.current = null;
		d3InstanceRef.current = null;
	},

	suppressNextDisciplineFitView: false,
	setSuppressNextDisciplineFitView: (value) => set({ suppressNextDisciplineFitView: value }),

	// ── Edit history ─────────────────────────────────────────────────────────
	past: [],
	future: [],

	pushHistory: (currentSnapshot, label) => {
		set((state) => ({
			past: [...state.past, { snapshot: currentSnapshot, label, timestamp: new Date() }],
			future: [],
		}));
	},

	undo: (presentSnapshot) => {
		const { past } = get();
		if (past.length === 0) return null;
		const previous = past[past.length - 1];
		set((state) => ({
			past: state.past.slice(0, -1),
			future: [
				{ snapshot: presentSnapshot, label: previous.label, timestamp: new Date() },
				...state.future,
			],
		}));
		return previous.snapshot;
	},

	redo: (presentSnapshot) => {
		const { future } = get();
		if (future.length === 0) return null;
		const next = future[0];
		set((state) => ({
			past: [
				...state.past,
				{ snapshot: presentSnapshot, label: next.label, timestamp: new Date() },
			],
			future: state.future.slice(1),
		}));
		return next.snapshot;
	},

	clearHistory: () => set({ past: [], future: [] }),
}));

export default useCourseMapStore;
